import { lazy, Suspense } from "react";
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PortalProvider } from "@/context/PortalContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import KaryawanLoginPage from "@/pages/KaryawanLoginPage";
import PortalLayout from "@/components/PortalLayout";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import DashboardLayout from "@/components/DashboardLayout";

import { isKasirRouteAllowed } from "@/lib/roleAccess";

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ProduksiPage = lazy(() => import("@/pages/ProduksiPage"));
const KaryawanPage = lazy(() => import("@/pages/KaryawanPage"));
const PayrollPage = lazy(() => import("@/pages/PayrollPage"));
const PayrollConfigPage = lazy(() => import("@/pages/PayrollConfigPage"));
const AccountingPage = lazy(() => import("@/pages/AccountingPage"));
const PiutangPelangganPage = lazy(() => import("@/pages/PiutangPelangganPage"));
const GlobalSearchResultsPage = lazy(() => import("@/pages/GlobalSearchResultsPage"));
const FinanceSalesPage = lazy(() => import("@/pages/FinanceSalesPage"));
const DriverCashbookPage = lazy(() => import("@/pages/DriverCashbookPage"));
const ShareholderPage = lazy(() => import("@/pages/ShareholderPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const PelangganPage = lazy(() => import("@/pages/PelangganPage"));
const DistribusiPage = lazy(() => import("@/pages/DistribusiPage"));
const SetoranPage = lazy(() => import("@/pages/SetoranPage"));
const SetoranPublicPage = lazy(() => import("@/pages/SetoranPublicPage"));
const ScanPage = lazy(() => import("@/pages/ScanPage"));
const OwnerDashboardPage = lazy(() => import("@/pages/OwnerDashboardPage"));
const KeuanganPage = lazy(() => import("@/pages/KeuanganPage"));
const BiayaPabrikPage = lazy(() => import("@/pages/BiayaPabrikPage"));
const UspAdminPage = lazy(() => import("@/pages/UspAdminPage"));
const LaporanUmumPage = lazy(() => import("@/pages/LaporanUmumPage"));
const LaporanDistribusiPage = lazy(() => import("@/pages/LaporanDistribusiPage"));
const LaporanKeuanganPage = lazy(() => import("@/pages/LaporanKeuanganPage"));
const LaporanProduksiPage = lazy(() => import("@/pages/LaporanProduksiPage"));
const UserManagementPage = lazy(() => import("@/pages/UserManagementPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const BahanBakuPage = lazy(() => import("@/pages/BahanBakuPage"));
const ShiftPage = lazy(() => import("@/pages/ShiftPage"));
const MuatanPage = lazy(() => import("@/pages/MuatanPage"));
const RepackReturPage = lazy(() => import("@/pages/RepackReturPage"));
const InsentifPage = lazy(() => import("@/pages/InsentifPage"));
const KinerjaDriverPage = lazy(() => import("@/pages/KinerjaDriverPage"));
const PenjualanPabrikPage = lazy(() => import("@/pages/PenjualanPabrikPage"));
const MesinPage = lazy(() => import("@/pages/MesinPage"));
const ChecklistPage = lazy(() => import("@/pages/ChecklistPage"));
const RepackPage = lazy(() => import("@/pages/RepackPage"));
const PortalKaryawanPage = lazy(() => import("@/pages/PortalKaryawanPage"));
const HRDPengajuanPage = lazy(() => import("@/pages/HRDPengajuanPage"));
const HPPPage = lazy(() => import("@/pages/HPPPage"));
const BBMArmadaPage = lazy(() => import("@/pages/BBMArmadaPage"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const MaintenancePublicPage = lazy(() => import("@/pages/MaintenancePublicPage"));
const HRMasterConfigPage = lazy(() => import("@/pages/HRMasterConfigPage"));
const HRPolicyConfigPage = lazy(() => import("@/pages/HRPolicyConfigPage"));
const HRContractTemplatePage = lazy(() => import("@/pages/HRContractTemplatePage"));
const HRIndividualContractsPage = lazy(() => import("@/pages/HRIndividualContractsPage"));
const HRTravelPage = lazy(() => import("@/pages/HRTravelPage"));
const HRLeavePage = lazy(() => import("@/pages/HRLeavePage"));
const HRAttendancePolicyPage = lazy(() => import("@/pages/HRAttendancePolicyPage"));
const HRTimekeepingPage = lazy(() => import("@/pages/HRTimekeepingPage"));
const HRLatenessReportPage = lazy(() => import("@/pages/HRLatenessReportPage"));
const HROvertimePage = lazy(() => import("@/pages/HROvertimePage"));
const HRReportingPage = lazy(() => import("@/pages/HRReportingPage"));
const HRAttendanceReportPage = lazy(() => import("@/pages/HRAttendanceReportPage"));

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "kasir" && !isKasirRouteAllowed(location.pathname)) {
    return <Navigate to="/" replace />;
  }
  if (roles && user.role !== "superadmin" && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <PortalProvider>
      <BrowserRouter>
        <Suspense fallback={<div role="status" className="min-h-screen flex items-center justify-center text-slate-600">Membuka halaman GEMA...</div>}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/karyawan/login" element={<KaryawanLoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/public/setoran/:id" element={<SetoranPublicPage />} />
          <Route path="/public/maintenance/:token" element={<MaintenancePublicPage />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalKaryawanPage />} />
          </Route>
          <Route path="/owner" element={<Protected roles={[]}><OwnerDashboardPage /></Protected>} />
          <Route path="/" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<DashboardPage />} />
            <Route path="usp" element={<Protected roles={["usp","hrd","akunting","auditor"]}><UspAdminPage /></Protected>} />
            <Route path="biaya-pabrik" element={<Protected roles={["admin","produksi","checker","kasir","akunting","auditor"]}><BiayaPabrikPage /></Protected>} />
            <Route path="produksi" element={<Protected roles={["admin","produksi","checker"]}><ProduksiPage /></Protected>} />
            <Route path="karyawan" element={<Protected roles={["admin","hrd"]}><KaryawanPage /></Protected>} />
            <Route path="payroll" element={<Protected roles={["admin","hrd","superadmin"]}><PayrollPage /></Protected>} />
            <Route path="payroll-config" element={<Protected roles={["admin","hrd","akunting","superadmin"]}><PayrollConfigPage /></Protected>} />
            <Route path="pencarian" element={<Protected><GlobalSearchResultsPage /></Protected>} />
            <Route path="pemegang-saham" element={<Protected roles={[]}><ShareholderPage /></Protected>} />
            <Route path="piutang-pelanggan" element={<Protected roles={["admin","kasir","akunting","auditor","distribusi"]}><PiutangPelangganPage /></Protected>} />
            <Route path="buku-setoran-driver" element={<Protected roles={["admin","kasir","akunting","auditor","distribusi"]}><DriverCashbookPage /></Protected>} />
            <Route path="rekonsiliasi-penjualan" element={<Protected roles={["admin","kasir","akunting","auditor"]}><FinanceSalesPage /></Protected>} />
            <Route path="accounting" element={<Protected roles={["admin","akunting","auditor","kasir","superadmin"]}><AccountingPage /></Protected>} />
            <Route path="inventory" element={<Protected roles={["admin","produksi","distribusi","kasir","akunting","auditor"]}><InventoryPage /></Protected>} />
            <Route path="pelanggan" element={<Protected roles={["admin","distribusi","kasir","driver"]}><PelangganPage /></Protected>} />
            <Route path="distribusi" element={<Protected roles={["admin","distribusi","kasir"]}><DistribusiPage /></Protected>} />
            <Route path="scan" element={<Protected roles={["admin","distribusi","kasir","armada","driver"]}><ScanPage /></Protected>} />
            <Route path="setoran" element={<Protected roles={["admin","distribusi","kasir","driver","armada"]}><SetoranPage /></Protected>} />
            <Route path="keuangan" element={<Protected roles={["admin","kasir","akunting"]}><KeuanganPage /></Protected>} />
            <Route path="laporan" element={<Navigate to="/laporan-umum" replace/>} />
            <Route path="laporan-umum" element={<Protected roles={["admin","superadmin","kasir","akunting","auditor"]}><LaporanUmumPage /></Protected>} />
            <Route path="laporan-produksi" element={<Protected roles={["admin","superadmin","produksi","kasir"]}><LaporanProduksiPage /></Protected>} />
            <Route path="laporan-distribusi" element={<Protected roles={["admin","superadmin","distribusi","kasir","akunting","auditor"]}><LaporanDistribusiPage /></Protected>} />
            <Route path="laporan-keuangan" element={<Protected roles={["admin","superadmin","kasir","akunting","auditor"]}><LaporanKeuanganPage /></Protected>} />
            <Route path="hpp" element={<Protected roles={["admin","akunting","auditor"]}><HPPPage /></Protected>} />
            <Route path="bbm-armada" element={<Protected roles={["admin","kasir"]}><BBMArmadaPage /></Protected>} />
            <Route path="maintenance/produksi" element={<Protected roles={["admin","produksi","checker"]}><MaintenancePage context="produksi"/></Protected>} />
            <Route path="maintenance/armada" element={<Protected roles={["admin","distribusi","kasir"]}><MaintenancePage context="armada"/></Protected>} />
            <Route path="users" element={<Protected roles={[]}><UserManagementPage /></Protected>} />
            <Route path="settings" element={<Protected roles={[]}><SettingsPage /></Protected>} />
            <Route path="bahan-baku" element={<Protected roles={["admin","produksi","checker","akunting","auditor"]}><BahanBakuPage /></Protected>} />
            <Route path="shift" element={<Protected roles={["admin","produksi","checker","hrd","auditor"]}><ShiftPage /></Protected>} />
            <Route path="muatan" element={<Protected roles={["admin","produksi","checker","distribusi","kasir","auditor","armada"]}><MuatanPage /></Protected>} />
            <Route path="repack-retur" element={<Protected roles={["admin","driver","armada","checker","produksi"]}><RepackReturPage /></Protected>} />
            <Route path="insentif" element={<Protected roles={["admin","akunting","kasir","auditor","distribusi","hrd"]}><InsentifPage /></Protected>} />
            <Route path="kinerja-driver" element={<Protected roles={["admin","distribusi","kasir","akunting","auditor","driver"]}><KinerjaDriverPage /></Protected>} />
            <Route path="penjualan-pabrik" element={<Protected roles={["admin","produksi","checker"]}><PenjualanPabrikPage /></Protected>} />
            <Route path="mesin" element={<Protected roles={["admin","superadmin","produksi"]}><MesinPage /></Protected>} />
            <Route path="checklist" element={<Protected roles={["admin","superadmin","produksi","checker","auditor"]}><ChecklistPage /></Protected>} />
            <Route path="repack" element={<Protected roles={["admin","superadmin","produksi","checker","auditor"]}><RepackPage /></Protected>} />
            <Route path="hrd-pengajuan" element={<Protected roles={["admin","superadmin","hrd"]}><HRDPengajuanPage /></Protected>} />
            <Route path="hr-master" element={<Protected roles={["hrd"]}><HRMasterConfigPage /></Protected>} />
            <Route path="hr-policies" element={<Protected roles={["admin","hrd"]}><HRPolicyConfigPage /></Protected>} />
            <Route path="hr-contract-templates" element={<Protected roles={["admin","hrd"]}><HRContractTemplatePage /></Protected>} />
            <Route path="hr-contracts" element={<Protected roles={["admin","hrd"]}><HRIndividualContractsPage /></Protected>} />
            <Route path="hr-travel" element={<Protected roles={["admin","hrd","akunting"]}><HRTravelPage /></Protected>} />
            <Route path="hr-leave" element={<Protected roles={["admin","hrd"]}><HRLeavePage /></Protected>} />
            <Route path="hr-attendance-policy" element={<Protected roles={["admin","hrd"]}><HRAttendancePolicyPage /></Protected>} />
            <Route path="hr-timekeeping" element={<Protected roles={["admin","hrd"]}><HRTimekeepingPage /></Protected>} />
            <Route path="hr-lateness-report" element={<Protected roles={["admin","hrd"]}><HRLatenessReportPage /></Protected>} />
            <Route path="hr-overtime" element={<Protected roles={["admin","hrd"]}><HROvertimePage /></Protected>} />
            <Route path="hr-attendance-report" element={<Protected roles={["admin","hrd"]}><HRAttendanceReportPage /></Protected>} />
            <Route path="hr-reporting" element={<Protected roles={["admin","hrd","akunting"]}><HRReportingPage /></Protected>} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </PortalProvider>
    </AuthProvider>
  );
}

export default App;
